import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from "react";
import { useQuery, useQueryClient, type QueryKey, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getEngagementReportCutoff } from "./adminAnalyticsRpc";

/**
 * Issue #872 review: every org reporting query is permission-dependent (only a
 * current admin of that org may see it), so its cache entry must belong to the
 * caller. This module is the one place those queries are built:
 *
 *  - Keys are ["org-report", <name>, <user id>, ...parts]. A different account
 *    never reads another account's entry, and a response that was in flight
 *    for the previous account lands under the previous account's key.
 *  - Queries run only once auth has resolved and there is a signed-in user.
 *  - A query that errors never re-presents an earlier success: `data` is
 *    undefined whenever `isError` is true.
 *  - ReportAuthProvider (mounted once in App) reads auth ONCE for every report
 *    hook beneath it, and cancels and removes every "org-report" entry when
 *    the signed-in user changes or signs out.
 */
export const ORG_REPORT_KEY = "org-report";

export function orgReportKey(name: string, userId: string | null, ...parts: unknown[]): QueryKey {
  return [ORG_REPORT_KEY, name, userId, ...parts];
}

interface ReportAuth {
  userId: string | null;
  isLoading: boolean;
}

const ReportAuthContext = createContext<ReportAuth | null>(null);

/**
 * One auth read shared by every report hook (each `useAuth()` registers its
 * own auth listener and session read, so a page of twelve widgets would make
 * twelve). It also owns the cache guard: when the signed-in user changes,
 * including sign-out, every protected report entry is cancelled and removed.
 */
export function ReportAuthProvider({ children }: { children: ReactNode }) {
  const { user, isLoading } = useAuth();
  const userId = user?.id ?? null;
  const value = useMemo(() => ({ userId, isLoading }), [userId, isLoading]);
  // The guard is the FIRST child, so its effect runs before any report hook
  // beneath it subscribes a query for the new user: React runs sibling
  // effects in order, and a hook's subscription made before the guard's
  // removal would be dropped with the stale entries and sit in "loading".
  return (
    <ReportAuthContext.Provider value={value}>
      <ReportCacheGuard />
      {children}
    </ReportAuthContext.Provider>
  );
}

function ReportCacheGuard() {
  const queryClient = useQueryClient();
  const { userId, isLoading } = useReportAuth();
  const previous = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (isLoading) return;
    if (previous.current !== undefined && previous.current !== userId) {
      void queryClient.cancelQueries({ queryKey: [ORG_REPORT_KEY] });
      queryClient.removeQueries({ queryKey: [ORG_REPORT_KEY] });
    }
    previous.current = userId;
  }, [isLoading, userId, queryClient]);

  return null;
}

function useReportAuth(): ReportAuth {
  const shared = useContext(ReportAuthContext);
  if (!shared) {
    throw new Error("Org report hooks must render inside <ReportAuthProvider> (mounted in App).");
  }
  return shared;
}

interface OrgReportOptions<T> {
  /** The org whose report this is; null means "not resolved yet". */
  orgId: string | null;
  /** Everything else the answer depends on (range bounds, bucket, ...). */
  parts?: unknown[];
  fetch: () => Promise<T>;
  enabled?: boolean;
}

export function useOrgReport<T>(name: string, { orgId, parts = [], fetch, enabled = true }: OrgReportOptions<T>) {
  const { userId, isLoading } = useReportAuth();
  const q = useQuery<T>({
    queryKey: orgReportKey(name, userId, orgId, ...parts),
    enabled: enabled && !isLoading && !!userId && !!orgId,
    queryFn: fetch,
    retry: false,
  });
  return { ...q, data: q.isError ? undefined : q.data };
}

interface PlatformReportOptions<T> {
  /** Everything the answer depends on besides the caller. */
  parts?: unknown[];
  fetch: () => Promise<T>;
  enabled?: boolean;
  staleTime?: number;
}

/**
 * A report that is not about one organization (the feed banner's platform
 * counters, #914). Same rules as useOrgReport: keyed by the signed-in user
 * under "org-report", so ReportAuthProvider drops it on an account change,
 * runs only with a signed-in user, and never re-presents data after an error.
 */
export function usePlatformReport<T>(
  name: string,
  { parts = [], fetch, enabled = true, staleTime }: PlatformReportOptions<T>,
) {
  const { userId, isLoading } = useReportAuth();
  const q = useQuery<T>({
    queryKey: orgReportKey(name, userId, ...parts),
    enabled: enabled && !isLoading && !!userId,
    queryFn: fetch,
    retry: false,
    staleTime,
  });
  return { ...q, data: q.isError ? undefined : q.data };
}

/** The server's report cutoff (first UTC date not yet reportable), "YYYY-MM-DD". */
export function useReportCutoff() {
  const { userId, isLoading } = useReportAuth();
  const q = useQuery<string>({
    queryKey: orgReportKey("cutoff", userId),
    enabled: !isLoading && !!userId,
    queryFn: async () => {
      const { data, error } = await getEngagementReportCutoff();
      if (error) throw error;
      if (typeof data !== "string") throw new Error("No report cutoff");
      return data;
    },
    retry: false,
    staleTime: 10 * 60 * 1000,
  });
  return { ...q, data: q.isError ? undefined : q.data };
}

type ReportState = Pick<UseQueryResult, "isError" | "isPending" | "isSuccess" | "fetchStatus">;

/** What a report widget knows about the organization it reports on. */
export interface OrgContextState {
  /** The org id, or null when there is none (resolved or not). */
  orgId: string | null;
  /** The org lookup is still resolving. */
  isLoading: boolean;
  /** The org lookup failed. */
  isError?: boolean;
}

export type DependentReportKind =
  /** Auth, the org lookup, the cutoff, or the report itself is still in flight. */
  | "loading"
  /** The org lookup, the cutoff read, or the report fetch failed. */
  | "error"
  /** There is no organization to report on: resolved, not a failure, not a zero. */
  | "unavailable"
  /** Both the cutoff and the report fetch succeeded; the data is measured. */
  | "ready";

/**
 * The state of a figure that needs an organization and the report cutoff
 * before its own fetch can run (issue #872 reviews). Each prerequisite is
 * read for what it is, so the widget never shows an empty or withheld state
 * for a prerequisite that is still loading, failed, or absent:
 *
 *   org loading                      → loading
 *   org lookup failed                → error
 *   no org (resolved)                → unavailable
 *   cutoff failed / report failed    → error
 *   cutoff pending / report fetching → loading
 *   report pending but idle          → loading (its prerequisites were just
 *                                      met; the fetch starts on the next tick)
 *   both succeeded                   → ready
 *
 * `fetchStatus === "idle"` alone never means "empty": a disabled query is
 * idle too, which is why the org and cutoff are inspected first.
 */
export function dependentReportState(org: OrgContextState, cutoff: ReportState, report: ReportState) {
  let kind: DependentReportKind;
  if (org.isError) kind = "error";
  else if (org.isLoading) kind = "loading";
  else if (!org.orgId) kind = "unavailable";
  else if (cutoff.isError || report.isError) kind = "error";
  else if (cutoff.isSuccess && report.isSuccess) kind = "ready";
  else kind = "loading";
  return {
    kind,
    isError: kind === "error",
    isLoading: kind === "loading",
    isUnavailable: kind === "unavailable",
    isSettled: kind === "ready",
  };
}
